// Geometry of the report status mark: the Vyce loader (assets/animations/loader-*.json,
// re-drawn frame for frame) and the 45° check it settles into, built from the same bars.
// Pure functions only, so the frames can be unit-tested and drawn by any renderer.

export interface Piece {
  /** Bottom-left corner the bar stands on, in loader units (166 × 166 comp). */
  px: number;
  py: number;
  w: number;
  /** Grows "up" from the anchor before the turn. */
  h: number;
  /** Clockwise turn about the anchor, degrees. */
  a: number;
  o: number;
}

export interface MarkFrame {
  pieces: Piece[];
  /** Turn of the whole mark, degrees. */
  deg: number;
  /** Translation that keeps the mark centred in the view box. */
  off: [number, number];
  scale: number;
  ring: { r: number; o: number } | null;
  /** 0 = loader colour, 1 = done colour. */
  colour: number;
}

/* ---------- easing ---------- */

export const cubicBezier = (x1: number, y1: number, x2: number, y2: number) => {
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const sampleX = (t: number) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t: number) => ((ay * t + by) * t + cy) * t;
  const slopeX = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  return (x: number) => {
    if (x <= 0) return 0;
    if (x >= 1) return 1;
    let t = x;
    for (let i = 0; i < 8; i++) {
      const error = sampleX(t) - x;
      const slope = slopeX(t);
      if (Math.abs(error) < 1e-5 || !slope) break;
      t -= error / slope;
    }
    return sampleY(t);
  };
};

const easeLottie = cubicBezier(0.42, 0, 0.58, 1);
const easeVyce = cubicBezier(0.2, 0.8, 0.2, 1);
const easeInOut = cubicBezier(0.65, 0, 0.35, 1);
const backOut = (t: number, s: number) => 1 + (s + 1) * Math.pow(t - 1, 3) + s * Math.pow(t - 1, 2);
const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
const clamp01 = (t: number) => Math.min(1, Math.max(0, t));
const span = (t: number, from: number, to: number) => clamp01((t - from) / (to - from));

/* ---------- timing (ms) ---------- */

export const LOADER_MOVE_MS = 500;
export const LOADER_LOOP_MS = 783;
export const MORPH_MS = 640;
export const MARK_SETTLED_MS = 1500;
export const REDUCED_FADE_MS = 200;

/* ---------- loader ---------- */

type Rect = [x: number, y: number, w: number, h: number, o: number];

// Top-left anchored rects at frame 0 and frame 30 of the 60 fps Lottie
const LOADER: { from: Rect; to: Rect }[] = [
  { from: [0, 56, 1, 1, 0], to: [16, 0, 40, 40, 1] },
  { from: [0, 95, 1, 1, 0], to: [16, 53, 40, 113, 1] },
  { from: [16, 0, 40, 40, 1], to: [71, 32, 40, 20, 1] },
  { from: [16, 53, 40, 113, 1], to: [71, 46, 40, 90, 1] },
  { from: [71, 32, 40, 104, 1], to: [126, 65, 40, 40, 1] },
  { from: [126, 65, 40, 40, 1], to: [166, 85, 1, 1, 0] },
];

export const PIECE_COUNT = LOADER.length;
export const LOADER_ANGLE = -30.5;
export const CHECK_ANGLE = -45;
export const VIEW_BOX = 220;

const fromRect = ([x, y, w, h, o]: Rect): Piece => ({ px: x, py: y + h, w, h, a: 0, o });

/** Loader pieces at `progress` (0..1, already eased) of one step. */
export const loaderPieces = (progress: number): Piece[] =>
  LOADER.map(({ from, to }) => fromRect(from.map((v, i) => lerp(v, to[i], progress)) as Rect));

/** Loader pieces at `ms` into its loop: a 500 ms move, then a hold. */
export const loaderPiecesAt = (ms: number): Piece[] => {
  const inLoop = ((ms % LOADER_LOOP_MS) + LOADER_LOOP_MS) % LOADER_LOOP_MS;
  return loaderPieces(easeLottie(inLoop < LOADER_MOVE_MS ? inLoop / LOADER_MOVE_MS : 1));
};

export const isLoaderMoving = (ms: number) =>
  ((ms % LOADER_LOOP_MS) + LOADER_LOOP_MS) % LOADER_LOOP_MS < LOADER_MOVE_MS;

/** The resting loader, where every step starts and ends. */
export const LOADER_REST = loaderPieces(1);

/* ---------- check ---------- */

const BAR = 40;
const SHORT = 70;
const LONG = 140;
const JOINT_X = 30;
const JOINT_Y = 150;

/** Pieces at `t` (0..1) of the morph from the resting loader to the check. */
export const morphPieces = (t: number): Piece[] => {
  const [topSquare, columnBar, midTop, midBottom, side, gone] = LOADER_REST;
  const tuck = easeInOut(span(t, 0, 300 / MORPH_MS));
  const joint = { x: JOINT_X + BAR / 2, y: JOINT_Y - BAR / 2 };

  // The two squares shrink into the joint and the tip, and fade
  const square = (p: Piece, target: { x: number; y: number }): Piece => {
    const w = lerp(p.w, 0, tuck);
    const h = lerp(p.h, 0, tuck);
    const cx = lerp(p.px + p.w / 2, target.x, tuck);
    const cy = lerp(p.py - p.h / 2, target.y, tuck);
    return { px: cx - w / 2, py: cy + h / 2, w, h, a: 0, o: 1 - tuck };
  };

  // The first column's bar stands at the joint as the short stroke
  const stand = backOut(easeVyce(span(t, 0, 560 / MORPH_MS)), 0.9);
  const shortStroke: Piece = {
    px: lerp(columnBar.px, JOINT_X, stand),
    py: lerp(columnBar.py, JOINT_Y, stand),
    w: BAR,
    h: lerp(columnBar.h, SHORT, stand),
    a: 0,
    o: 1,
  };

  // The middle column (two Lottie layers, one bar on screen) topples 90° on its foot
  const topple = span(t, 60 / MORPH_MS, 1);
  const move = easeInOut(topple);
  const columnHeight = midBottom.py - (midTop.py - midTop.h);
  const longStroke: Piece = {
    px: lerp(midBottom.px, JOINT_X, move),
    py: lerp(midBottom.py, JOINT_Y - BAR, move),
    w: BAR,
    h: lerp(columnHeight, LONG, move),
    a: backOut(easeInOut(topple), 1) * 90,
    o: 1,
  };

  return [
    square(topSquare, joint),
    shortStroke,
    { ...midTop, o: 0 },
    longStroke,
    square(side, { x: JOINT_X + LONG - BAR / 2, y: JOINT_Y - BAR / 2 }),
    { ...gone, o: 0 },
  ];
};

/* ---------- projection ---------- */

export const pieceCorners = (p: Piece): [number, number][] => {
  const r = (p.a * Math.PI) / 180;
  const c = Math.cos(r);
  const s = Math.sin(r);
  const right: [number, number] = [c, s];
  const up: [number, number] = [s, -c];
  const base: [number, number] = [p.px, p.py];
  const far: [number, number] = [base[0] + right[0] * p.w, base[1] + right[1] * p.w];
  return [
    base,
    far,
    [far[0] + up[0] * p.h, far[1] + up[1] * p.h],
    [base[0] + up[0] * p.h, base[1] + up[1] * p.h],
  ];
};

const rotate = ([x, y]: [number, number], deg: number): [number, number] => {
  const r = (deg * Math.PI) / 180;
  return [x * Math.cos(r) - y * Math.sin(r), x * Math.sin(r) + y * Math.cos(r)];
};

const isVisible = (p: Piece) => p.o > 0.01 && p.w > 0.3 && p.h > 0.3;

const centreOffset = (pieces: Piece[], deg: number): [number, number] => {
  let x0 = Infinity;
  let y0 = Infinity;
  let x1 = -Infinity;
  let y1 = -Infinity;
  pieces.filter(isVisible).forEach(p =>
    pieceCorners(p).forEach(point => {
      const [x, y] = rotate(point, deg);
      x0 = Math.min(x0, x);
      y0 = Math.min(y0, y);
      x1 = Math.max(x1, x);
      y1 = Math.max(y1, y);
    })
  );
  return [VIEW_BOX / 2 - (x0 + x1) / 2, VIEW_BOX / 2 - (y0 + y1) / 2];
};

const REST_OFFSET = centreOffset(LOADER_REST, LOADER_ANGLE);
const CHECK_OFFSET = centreOffset(morphPieces(1), CHECK_ANGLE);

export const loaderFrame = (ms: number): MarkFrame => ({
  pieces: loaderPiecesAt(ms),
  deg: LOADER_ANGLE,
  off: REST_OFFSET,
  scale: 1,
  ring: null,
  colour: 0,
});

/**
 * The mark `ms` after the morph started: 640 ms of motion, a landing pop and ring,
 * and a purple → green recolour from 400 to 900 ms.
 */
export const morphFrame = (ms: number): MarkFrame => {
  const t = clamp01(ms / MORPH_MS);
  const turn = easeVyce(t);
  const landed = ms - MORPH_MS;
  let scale = 1;
  let ring: MarkFrame['ring'] = null;
  if (landed > 0) {
    scale = 1 + 0.08 * Math.sin(Math.PI * clamp01(landed / 260));
    const fade = clamp01(landed / 600);
    ring = { r: lerp(70, 104, easeVyce(fade)), o: 0.3 * (1 - fade) };
  }
  return {
    pieces: morphPieces(t),
    deg: lerp(LOADER_ANGLE, CHECK_ANGLE, turn),
    off: [lerp(REST_OFFSET[0], CHECK_OFFSET[0], turn), lerp(REST_OFFSET[1], CHECK_OFFSET[1], turn)],
    scale,
    ring,
    colour: easeInOut(span(ms, 400, 900)),
  };
};

/** SVG `points` for one piece, or '' when it is not drawn. */
export const piecePoints = (p: Piece, frame: Pick<MarkFrame, 'deg' | 'off' | 'scale'>): string => {
  if (!isVisible(p)) return '';
  const centre = VIEW_BOX / 2;
  return pieceCorners(p)
    .map(point => {
      const [rx, ry] = rotate(point, frame.deg);
      const x = centre + (rx + frame.off[0] - centre) * frame.scale;
      const y = centre + (ry + frame.off[1] - centre) * frame.scale;
      return `${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
};

/* ---------- colour ---------- */

const hexToRgb = (hex: string): [number, number, number] => {
  const value = hex.trim().replace('#', '');
  const full = value.length === 3 ? value.split('').map(c => c + c).join('') : value;
  const n = parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
};

/** Mixes two theme hex colours in sRGB; `k` 0 = from, 1 = to. */
export const mixColours = (from: string, to: string, k: number) => {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  return `rgb(${a.map((c, i) => Math.round(lerp(c, b[i], k))).join(',')})`;
};
